import { useClerk } from "@clerk/tanstack-react-start";
import { createFileRoute } from "@tanstack/react-router";

/**
 * ClawInstall customer logout page
 *
 * Signs the customer out of Clerk and lands them, signed out, on the public
 * sign-in page. Reachable at `clawinstall.ai/logout` (the domain router maps
 * the public `/logout` onto the internal `/clawinstall/logout`, mirroring the
 * `/login` route). Signing out an already-signed-out Clerk instance is a
 * harmless no-op redirect.
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/logout/")({
  component: ClawInstallLogout,
});

/**
 * Module-scoped one-shot guard (mirrors `ClerkAuthSync.clerkListenerState`) so
 * we call `signOut` exactly once per Clerk instance without `useEffect`
 * (forbidden in `@fm/web`). Scheduled via `queueMicrotask` so nothing mutates
 * React/atom state during render.
 *
 * Exported only so a unit test can reset it between cases.
 *
 * @since 1.0.0
 */
export const logoutState = { signedOutFrom: null as unknown };

export function ClawInstallLogout() {
  const clerk = useClerk();
  if (clerk !== logoutState.signedOutFrom) {
    logoutState.signedOutFrom = clerk;
    queueMicrotask(() => {
      // Clerk performs the post-sign-out browser navigation itself. `/login`
      // is the public brand path (the domain router maps it to
      // `/clawinstall/login`), matching the login route's `<SignIn path>`.
      void clerk.signOut({ redirectUrl: "/login" });
    });
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-6">
      <p className="text-on-surface">Signing you out...</p>
    </div>
  );
}
